import { DecimalPipe, NgClass, PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AmountPipe } from '../../../../../core/pipes/amount.pipe';
import { AuthService } from '../../../../auth/auth.service';
import { DashboardEngineService } from '../../../services/dashboard-engine.service';

@Component({
  selector: 'app-financial-summary-card',
  standalone: true,
  imports: [DecimalPipe, PercentPipe, NgClass, AmountPipe, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './financial-summary-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinancialSummaryCardComponent implements OnChanges {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() dashboardType!: string;
  @Input() widgetId = 'financialHealth';
  @Input() filters: any = {};
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  data: any = null;
  loading = false;
  error: string | null = null;

  protected httpError(err: any): string {
    if (err.status === 403) return 'Access restricted';
    if (err.status === 500) return 'Service unavailable';
    return 'Unable to load data';
  }

  get collectionPct(): number {
    return this.data?.collection_pct ?? 0;
  }
  get collectionColor(): string {
    if (this.collectionPct >= 90) return 'text-green-600';
    if (this.collectionPct >= 70) return 'text-yellow-600';
    return 'text-red-600';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasPermission) return;
    if (this.dashboardType) this.fetchData();
  }

  private fetchData(): void {
    this.loading = true;
    this.engine
      .getWidgetData(this.dashboardType, this.widgetId, this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.data = res.record?.data ?? res.record ?? res;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = this.httpError(err);
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }
}
